import { isEmpty } from 'lodash';
import { ConfigurationSpreadsheets } from 'common/types/scalarSpreadsheet';
import { ValuationsApproach } from 'common/types/valuation';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import { underScoreSpaces } from 'pages/Allocation/allocations/utilities/util';
import { getApproachTableName } from 'pages/ValuationsAllocation/util';
import { getArrayValue } from 'utilities';
import { SecurityWithValue } from '../types';
import {
  FUTURE_VALUES_PER_SHARE_TABLE_NAME,
  FUTURE_VALUES_PER_SHARE_TABLE_SLUG,
  PRESENT_VALUES_PER_SHARE_TABLE_NAME,
  PRESENT_VALUES_PER_SHARE_TABLE_SLUG,
} from './config/constants';
import createColumns from './config/createColumns';
import customParser from './config/customParser';
import getRowConfig from './config/rowConfig';

const presentTableTerms = Object.freeze({
  tableName: PRESENT_VALUES_PER_SHARE_TABLE_NAME,
  tableSlug: PRESENT_VALUES_PER_SHARE_TABLE_SLUG,
});

const futureTableTerms = Object.freeze({
  tableName: FUTURE_VALUES_PER_SHARE_TABLE_NAME,
  tableSlug: FUTURE_VALUES_PER_SHARE_TABLE_SLUG,
});

export interface CreateValuesPerShareSpreadsheetsParams {
  approach: ValuationsApproach;
  futureValuesPerShare?: SecurityWithValue[];
  presentValuesPerShare?: SecurityWithValue[];
}

const createValuesPerShareSpreadsheets = (params: CreateValuesPerShareSpreadsheetsParams) => {
  const { approach, futureValuesPerShare = [], presentValuesPerShare = [] } = params;

  const presentValuesPerShareName = getApproachTableName({
    approach,
    tableSuffix: underScoreSpaces(PRESENT_VALUES_PER_SHARE_TABLE_NAME),
  });

  const futureValuesPerShareName = getApproachTableName({
    approach,
    tableSuffix: underScoreSpaces(FUTURE_VALUES_PER_SHARE_TABLE_NAME),
  });

  const presentValuesPerShareColumns = getArrayValue(createColumns(presentValuesPerShare || []));
  const futureValuesPerShareColumns = getArrayValue(createColumns(futureValuesPerShare || []));

  const rowConfig = ({ isFutureValuesPerShare }: any) =>
    getRowConfig({
      securities: isFutureValuesPerShare ? (futureValuesPerShare || []) : (presentValuesPerShare || []),
      isFutureValuesPerShare,
    });

  const parser = (parserParams: any) => customParser(parserParams);

  const commonSheetConfig = {
    allowConfirmAndDeleteColumn: false,
    alwaysDisplayLegend: false,
    currencyFormatter: true,
    format: undefined,
    parser,
    showToolbar: false,
    showTotalColumn: false,
    totalParser: undefined,
    unitsFormatter: true,
    colTitleRow: 0,
    showPreviousColsDivider: false,
    showTitlesColumn: true,
    hasColTitle: true,
  } as Partial<SpreadsheetConfig>;

  const presentValuesPerShareSheet = !isEmpty(presentValuesPerShareColumns)
    ? new SpreadsheetConfig({
      ...commonSheetConfig,
      columns: presentValuesPerShareColumns,
      name: presentValuesPerShareName,
      rowConfig: rowConfig({ isFutureValuesPerShare: false }),
      tableData: { columns: presentValuesPerShareColumns },
      tableTerms: presentTableTerms as unknown as SpreadsheetConfig['tableTerms'],
    }) as unknown as ConfigurationSpreadsheets
    : null;

  const futureValuesPerShareSheet = !isEmpty(futureValuesPerShareColumns)
    ? new SpreadsheetConfig({
      ...commonSheetConfig,
      columns: futureValuesPerShareColumns,
      name: futureValuesPerShareName,
      rowConfig: rowConfig({ isFutureValuesPerShare: true }),
      tableData: { columns: futureValuesPerShareColumns },
      tableTerms: futureTableTerms as unknown as SpreadsheetConfig['tableTerms'],
    }) as unknown as ConfigurationSpreadsheets
    : null;

  return {
    presentValuesPerShareSheet,
    futureValuesPerShareSheet,
  };
};

export default createValuesPerShareSpreadsheets;
